import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import { Locale, SUPPORTED_LOCALES } from "@/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LABELS: Record<Locale, string> = {
  it: "IT",
  en: "EN",
  ru: "RU",
  fr: "FR",
  es: "ES",
  pt: "PT",
  ar: "AR",
  zh: "ZH",
  hi: "HI",
};

export default function LanguageSwitcher({ transparent = false }: { transparent?: boolean }) {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const current = (i18n.language as Locale) || "it";

  const switchTo = (next: Locale) => {
    if (next === current) return;
    const segments = location.pathname.split("/");
    if (SUPPORTED_LOCALES.includes(segments[1] as Locale)) {
      segments[1] = next;
    } else {
      segments.splice(1, 0, next);
    }
    const newPath = segments.join("/").replace(/\/+/g, "/") || "/";
    i18n.changeLanguage(next);
    navigate(newPath + location.search + location.hash);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={
          "flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase transition " +
          (transparent
            ? "text-white/85 hover:text-white"
            : "text-foreground/80 hover:text-foreground")
        }
      >
        <Globe className="h-4 w-4" />
        {LABELS[current]}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-0 w-[64px] p-1">
        {SUPPORTED_LOCALES.map((l) => (
          <DropdownMenuItem
            key={l}
            onClick={() => switchTo(l)}
            className={
              "uppercase tracking-[0.15em] text-[12px] cursor-pointer justify-center px-2 py-1.5 " +
              (l === current ? "text-[hsl(var(--accent))] font-semibold" : "")
            }
          >
            {LABELS[l]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}